import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from 'sonner';
import Header from './components/Header';
import Footer from './components/Footer';
import AdSlot from './components/AdSlot';
import DefaultSeo from './components/DefaultSeo';
import Home from './pages/Home';
import HomePhotoGrid from './pages/HomePhotoGrid';
import Bookmarks from './pages/Bookmarks';
import Admin from './pages/Admin';
import Analytics from './pages/Analytics';
import Trending from './pages/Trending';
import Leaderboard from './pages/Leaderboard';
import Auth from './pages/Auth';
import ArticlePage from './pages/ArticlePage';
import ResetPassword from './pages/ResetPassword';
import MyStory from './pages/MyStory';
import './index.css';

// Toggle this to switch between layouts: 'photo-grid' or 'classic'
const HOME_LAYOUT = 'classic';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FF6B6B] border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FF6B6B] border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppContent = () => {
  const HomeComponent = HOME_LAYOUT === 'photo-grid' ? HomePhotoGrid : Home;
  
  return (
    <div className="min-h-screen bg-[#FFFDF5] dark:bg-[#1a1816] flex flex-col">
      <DefaultSeo />
      <Header />
      {/* Leaderboard ad slot just below the navigation */}
      <div className="px-4 pt-3">
        <AdSlot variant="leaderboard" />
      </div>
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<HomeComponent />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/trending" element={<Trending />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/my-story" element={<MyStory />} />
          <Route
            path="/bookmarks"
            element={
              <ProtectedRoute>
                <Bookmarks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <Admin />
              </AdminRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <AdminRoute>
                <Analytics />
              </AdminRoute>
            }
          />
          <Route path="/article/:newsId" element={<ArticlePage />} />
          <Route path="/news/:slug" element={<ArticlePage />} />
        </Routes>
      </div>
      <Footer />
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#FFFFFF',
            border: '2px solid #000000',
            borderRadius: '12px',
            boxShadow: '4px 4px 0px 0px rgba(0, 0, 0, 1)',
            fontFamily: 'Manrope, sans-serif',
            fontWeight: '500'
          }
        }}
      />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;